/**
 * ConversationMode — always-on, hands-free voice chat with ARIA.
 *
 * State machine: IDLE -> LISTENING -> RECORDING -> TRANSCRIBING (Whisper)
 *   -> THINKING (chat SSE) -> SPEAKING (TTS) -> LISTENING (loop).
 * Mic + VAD live in hooks/useVoiceActivity; visuals in conversation/ConversationStage.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { streamChat } from "../lib/sse";
import { useVoiceActivity } from "../hooks/useVoiceActivity";
import { ConversationStage, STATE } from "./conversation/ConversationStage";
import { toast } from "sonner";

const WAKE_RE = /\baria[ ,.!?]/;
const passesWakeWord = (heard) => {
  const lc = heard.toLowerCase();
  return lc.startsWith("aria") || lc.startsWith("hey aria") || WAKE_RE.test(lc.slice(0, 20));
};

export default function ConversationMode({
  isOpen, onClose, orgConfig, wakeWordRequired, autoTTS = true,
  docFilter, activeConversationId, onConversationUpdate, onReload,
  projectId, interviewId, applicationId,
}) {
  const [state, setState] = useState(STATE.IDLE);
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const audioElRef = useRef(null);
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const playReply = useCallback(async (text) => {
    setState(STATE.SPEAKING);
    try {
      const { data } = await api.post("/tts", { text }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (!audioElRef.current) return;
      audioElRef.current.src = url;
      audioElRef.current.onended = () => { URL.revokeObjectURL(url); setState(STATE.LISTENING); };
      audioElRef.current.onerror = () => setState(STATE.LISTENING);
      await audioElRef.current.play();
    } catch { setState(STATE.LISTENING); }
  }, []);

  const sendToChat = useCallback(async (question) => {
    setState(STATE.THINKING);
    setReply("");
    let collected = "";
    try {
      await streamChat({
        conversation_id: activeConversationId, message: question,
        project_id: projectId, interview_id: interviewId, application_id: applicationId,
        document_id: docFilter && docFilter !== "all" ? docFilter : null, mode: "voice",
      }, (name, d) => {
        if (name === "meta") onConversationUpdate?.(d.conversation_id);
        else if (name === "token") { collected += d.t; setReply(collected); }
      });
      onReload?.();
      if (autoTTS && collected) await playReply(collected);
      else setState(STATE.LISTENING);
    } catch {
      toast.error("Chat failed — listening again");
      setState(STATE.LISTENING);
    }
  }, [activeConversationId, autoTTS, docFilter, onConversationUpdate, onReload, playReply, projectId, interviewId, applicationId]);

  const onClip = useCallback(async (blob) => {
    if (!blob) { setState(STATE.LISTENING); return; }
    setState(STATE.TRANSCRIBING);
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      const { data } = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const heard = (data.text || "").trim();
      setTranscript(heard);
      if (!heard || (wakeWordRequired && !passesWakeWord(heard))) { setState(STATE.LISTENING); return; }
      await sendToChat(heard);
    } catch {
      toast.error("Couldn't transcribe — listening again");
      setState(STATE.LISTENING);
    }
  }, [wakeWordRequired, sendToChat]);

  const { start, stop, waveform } = useVoiceActivity({
    armed: () => stateRef.current === STATE.LISTENING || stateRef.current === STATE.RECORDING,
    onClip,
    onSpeechStart: () => setState(STATE.RECORDING),
    onError: () => { toast.error("Microphone access denied"); setState(STATE.ERROR); },
  });

  useEffect(() => {
    if (!isOpen) { stop(); setState(STATE.IDLE); setTranscript(""); setReply(""); return; }
    start().then((ok) => { if (ok) setState(STATE.LISTENING); });
    return () => stop();
  }, [isOpen, start, stop]);

  if (!isOpen) return null;
  return (
    <>
      <ConversationStage state={state} transcript={transcript} reply={reply} waveform={waveform}
        accent={orgConfig?.accent_color || "#2563FF"} nickname={orgConfig?.aria_nickname || "ARIA"}
        wakeWordRequired={wakeWordRequired} onClose={onClose} />
      <audio ref={audioElRef} hidden />
    </>
  );
}
